import { useState, useEffect } from 'react';

const bingoSquares = [
  "Let me check with my manager",
  "This deal expires tonight",
  "Nitrogen tires — $299",
  "We can't show you the invoice",
  "Four-square worksheet appears",
  "Mandatory paint protection",
  "Doc fee surprise at signing",
  "Trade-in lowball offer",
  "Finance office 'just one more thing'",
  "That car sold this morning",
  "Market adjustment fee",
  "We'll throw in free floor mats",
  "Credit app before test drive",
  "Monthly payment focus instead of price",
  "Mandatory GAP insurance",
  "Free CARFAX — that we pulled ourselves"
];

export default function DealerBingo() {
  const [daubed, setDaubed] = useState<boolean[]>(new Array(16).fill(false));
  const [showBingo, setShowBingo] = useState(false);

  const toggleSquare = (index: number) => {
    const newDaubed = [...daubed];
    newDaubed[index] = !newDaubed[index];
    setDaubed(newDaubed);
  };

  const resetCard = () => {
    setDaubed(new Array(16).fill(false));
    setShowBingo(false);
  };

  useEffect(() => {
    // Check for bingo (row, column, or diagonal)
    const checkBingo = () => {
      // Check rows
      for (let i = 0; i < 4; i++) {
        if (daubed[i * 4] && daubed[i * 4 + 1] && daubed[i * 4 + 2] && daubed[i * 4 + 3]) {
          return true;
        }
      }
      
      // Check columns
      for (let i = 0; i < 4; i++) {
        if (daubed[i] && daubed[i + 4] && daubed[i + 8] && daubed[i + 12]) {
          return true;
        }
      }
      
      // Check diagonals
      if (daubed[0] && daubed[5] && daubed[10] && daubed[15]) {
        return true;
      }
      if (daubed[3] && daubed[6] && daubed[9] && daubed[12]) {
        return true;
      }
      
      return false;
    };

    if (checkBingo() && !showBingo) {
      setShowBingo(true);
    } else if (!checkBingo() && showBingo) {
      setShowBingo(false);
    }
  }, [daubed, showBingo]);

  return (
    <section className="py-16 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Dealer Bingo
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Click the squares you've experienced. Get four in a row and you've earned our sympathy.
          </p>
        </div>

        {/* Bingo Grid */}
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-8">
          <div className="grid grid-cols-4 gap-2 sm:gap-3 mb-6">
            {bingoSquares.map((square, index) => (
              <button
                key={index}
                onClick={() => toggleSquare(index)}
                className={`
                  relative aspect-square rounded-lg border-2 p-2 sm:p-3
                  flex items-center justify-center text-center
                  transition-all duration-200 cursor-pointer
                  ${
                    daubed[index]
                      ? 'bg-red-500 border-red-600 text-white shadow-md'
                      : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400 hover:shadow-sm'
                  }
                `}
              >
                <span className="text-xs sm:text-sm font-medium leading-tight">
                  {square}
                </span>
                {daubed[index] && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <i className="ri-check-line text-3xl sm:text-4xl text-white opacity-30"></i>
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Reset Button */}
          <div className="flex justify-center">
            <button
              onClick={resetCard}
              className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors whitespace-nowrap"
            >
              Reset Card
            </button>
          </div>
        </div>

        {/* Bingo Alert */}
        {showBingo && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-8 text-center animate-[pulse_1s_ease-in-out_3]">
            <div className="flex items-center justify-center gap-3 mb-2">
              <i className="ri-emotion-sad-line text-3xl text-red-600"></i>
              <h3 className="text-2xl font-bold text-red-900">BINGO!</h3>
              <i className="ri-emotion-sad-line text-3xl text-red-600"></i>
            </div>
            <p className="text-red-700 font-medium">
              You've experienced a full dealership. We're so sorry.
            </p>
          </div>
        )}

        {/* Shareable CTA */}
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-8 text-center text-white">
          <i className="ri-share-line text-4xl mb-4"></i>
          <h3 className="text-2xl font-bold mb-2">
            Screenshot Your Card
          </h3>
          <p className="text-teal-50 mb-6 max-w-md mx-auto">
            Share your dealership horror story. Tag us and let the world know what you've survived.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href="https://www.facebook.com/sharer/sharer.php?u=https://rockhoundautos.com/compare"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-white text-teal-600 rounded-lg font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-facebook-fill mr-2"></i>
              Share on Facebook
            </a>
            <a
              href="https://twitter.com/intent/tweet?text=I%20just%20played%20Dealer%20Bingo%20and%20won%20%28lost%3F%29.%20Check%20out%20how%20Rockhound%20Autos%20does%20it%20differently%3A&url=https://rockhoundautos.com/compare"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 bg-white text-teal-600 rounded-lg font-semibold hover:bg-teal-50 transition-colors whitespace-nowrap"
            >
              <i className="ri-twitter-x-fill mr-2"></i>
              Share on X
            </a>
          </div>
        </div>

        {/* Bottom Note */}
        <p className="text-center text-sm text-gray-500 mt-8">
          Every square is based on real buyer experiences. If you've checked more than four, you deserve a medal. Or at least a better car-buying experience.
        </p>
      </div>
    </section>
  );
}
